import { useCallback, useEffect, useState } from "react";
import { ArrowsClockwise, Sparkle, TrendUp, TrendDown } from "@phosphor-icons/react";
import api from "../../lib/api";

const TIER_META = {
  double_down: { label: "DOUBLE DOWN", color: "bg-emerald-50 text-emerald-700 border-emerald-300", icon: TrendUp },
  hold:        { label: "HOLD",        color: "bg-zinc-50 text-zinc-700 border-zinc-300",        icon: null },
  monitor:     { label: "MONITOR",     color: "bg-amber-50 text-amber-700 border-amber-300",     icon: null },
  cut:         { label: "CUT",         color: "bg-red-50 text-[#D0021B] border-red-300",         icon: TrendDown },
};

export default function RecommendationsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/leads/recommendations");
      setData(data);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.warn("Recommendations load failed", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!data) {
    return (
      <section className="border border-zinc-950 mt-6" data-testid="recommendations-panel">
        <div className="border-b border-zinc-950 bg-zinc-950 text-white px-4 py-2 font-mono text-[11px] tracking-[0.2em] uppercase flex items-center gap-2">
          <Sparkle size={12} /> ai recommendations
        </div>
        <div className="p-6 text-xs text-zinc-400 font-mono">{loading ? "ANALYZING…" : "NO DATA"}</div>
      </section>
    );
  }

  return (
    <section className="border border-zinc-950 mt-6" data-testid="recommendations-panel">
      <div className="border-b border-zinc-950 bg-zinc-950 text-white px-4 py-2 font-mono text-[11px] tracking-[0.2em] uppercase flex items-center justify-between">
        <span className="flex items-center gap-2"><Sparkle size={12} /> ai recommendations</span>
        <button onClick={load} className="hover:text-[#FF3E00] transition-colors" data-testid="recommendations-refresh-btn"><ArrowsClockwise size={12} /></button>
      </div>

      {data.narrative && (
        <div className="p-5 bg-zinc-50 border-b border-zinc-200">
          <div className="label-sm text-zinc-500 mb-2 flex items-center gap-1.5"><Sparkle size={11} /> GROWTH MEMO · CLAUDE</div>
          <p className="text-sm leading-relaxed whitespace-pre-wrap text-zinc-700" data-testid="recommendations-narrative">{data.narrative}</p>
        </div>
      )}

      <div className="p-5">
        <div className="label-sm text-zinc-500 mb-2">ROI Scoreboard ({data.scoreboard.length} sources)</div>
        <div className="space-y-1.5" data-testid="recommendations-scoreboard">
          {data.scoreboard.map((row) => {
            const tier = TIER_META[row.tier] || TIER_META.hold;
            const Icon = tier.icon;
            return (
              <div key={row.ref} className="flex items-center gap-3 text-sm py-2 border-b border-zinc-100" data-testid={`scoreboard-${row.ref}`}>
                <span className={`text-[9px] font-mono px-1.5 py-0.5 border ${tier.color} flex items-center gap-1`}>
                  {Icon && <Icon size={9} weight="bold" />} {tier.label}
                </span>
                <span className="font-mono text-xs flex-1">{row.ref}</span>
                <span className="font-mono text-[10px] text-zinc-500 tnum w-16 text-right">{row.lead_count} leads</span>
                <span className="font-mono text-[10px] text-zinc-500 tnum w-20 text-right">avg {row.avg_score}/100</span>
                <span className="font-mono text-[10px] text-zinc-500 tnum w-24 text-right">${row.avg_revenue.toLocaleString()}/mo</span>
                <span className="font-display font-bold tnum w-28 text-right">${row.roi_proxy.toLocaleString()}</span>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
